<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <GeneralDialogTemplate @close="closeDialog">
        <v-row
            v-if="is_loading === true"
            align="center"
            justify="center"
        >
            <LoaderDefault/>
        </v-row>
        <template slot="header">
            <span class="font-weight-medium title">{{ $t("Edit jobs") }}</span>
        </template>
        <form v-if="is_loading === false">
            <v-row no-gutters>
                <v-col cols="12">
                    <v-radio-group v-model="form_data.action" :error-messages="form_data.action_error" class="mt-2" row>
                        <v-radio :label="$t('Add')" color="primary" value="add_job"></v-radio>
                        <v-radio :label="$t('Replace plan tasks with job')" color="primary" value="replace_jobs">
                        </v-radio>
                    </v-radio-group>
                </v-col>
                <v-col cols="12">
                    <dropdown
                        :items="templates"
                        :selected="templates.filter(x => x.id === form_data.template_id)"
                        name-key="name"
                        value-key="id"
                        show-selected-first
                        :label="$t('Select template')"
                        :error-messages="form_data.template_id_error"
                        @input="form_data.template_id = $event.id"
                        :disabled="is_loading"
                    />
                </v-col>
                <v-col cols="12">
                    <dropdown
                        :items="users"
                        :selected="users.filter(x => x.id === form_data.person_id)"
                        name-key="name"
                        value-key="id"
                        show-selected-first
                        icon-key="icon"
                        icon-color="secondary"
                        :label="$t('Select') + ' ' + $t('member')"
                        :error-messages="form_data.person_id_error"
                        @input="form_data.person_id = $event.id"
                        :disabled="is_loading"
                    />
                    <span class="body-2 grey--text">
                        {{ $t("Tasks with user will be added to every selected client") }}
                    </span>
                </v-col>
                <v-col
                    v-if="form_data.action === 'add_job'"
                    cols="12"
                >
                    <v-checkbox
                        v-model="form_data.force"
                        :label="$t('Add even if job already applied')"
                        class="mt-3"
                        color="primary"
                        hide-details
                        :disabled="is_loading"
                    />
                </v-col>
            </v-row>
        </form>
        <template
            v-if="is_loading === false"
            slot="footer"
        >
            <v-spacer/>
            <v-btn
                color="primary"
                text
                @click="closeDialog()"
            >
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn
                class="px-5"
                color="primary inverted--text"
                @click="onSaveClick()"
            >
                {{ $t("Save") }}
            </v-btn>
        </template>
    </GeneralDialogTemplate>
</template>

<script>
import Vue from "vue";
import {computed, ref} from "@vue/composition-api";
import DialogModule from "../../../store/modules/dialog";
import ClientModule from "../../../store/modules/client";
import AppModule from "../../../store/modules/app";
import {$t} from "@/i18n";
import LoaderDefault from '@/components/LoaderDefault.vue'
import errorDialog from "@/views/ErrorDialog/errorDialog"
import GeneralDialogTemplate from "../../Dialog/GeneralDialogTemplate.vue";
import {useMemberStore} from "../../../store/modules/member";

export default {
    name: "BulkEditClientJobsDialog",
    components: {
        GeneralDialogTemplate,
        LoaderDefault,
    },
    props: {
        dialogIndex: {
            type: Number,
            default: null
        }
    },

    setup(props) {
        const is_loading = ref(false);

        const {dialogs, closeDialog, setConfirmOptions, openConfirmDialog} = DialogModule();
        const memberStore = useMemberStore()
        const {
            fetchClassifiers,
            account_job_names,
            handleErrorResponse,
            clearValidationErrors,
            hasValidationErrors
        } = AppModule();
        const {executeClientsAction} = ClientModule();

        const form_data = ref(null);
        const loading = false;

        const dialogPayload = dialogs.value[props.dialogIndex].payload
        const account_id = computed(() => dialogPayload.account_id);
        const client_id_list = computed(() => dialogPayload.client_id_list);

        const load_data = function () {
            fetchClassifiers({
                account_id: account_id.value,
                force: {
                    default: true,
                    types: ["account_job_names"]
                }
            })
                .catch(ex => errorDialog.showError(ex));

            form_data.value = Vue.prepare_form_data(
                {
                    action: "add_job",
                    company_id: account_id.value,
                    person_id: null,
                    template_id: null,
                    client_id_list: client_id_list.value,
                    force: null,
                },
                {
                    action: "choice_single",
                    person_id: "reference",
                    template_id: "reference",
                }
            );
        };

        const templates = computed(function () {
            if (account_job_names.value[account_id.value] === undefined) return [];
            return account_job_names.value[account_id.value].sort((a, b) => (a.name > b.name ? 1 : -1));
        });

        const users = computed(function () {
            let users_tmp = memberStore.getMembersByCompany(account_id.value)

            users_tmp.unshift({
                name: $t("Default user"),
                id: 0,
                person_id: 0,
                client_initials: "",
                color: "grey",
                icon: "Star"
            });

            return users_tmp;
        });

        const onConfirmClick = function () {
            let data = Vue.clean_and_clone_object(form_data.value);

            executeClientsAction(data)
                .then(function () {
                    closeDialog();
                })
                .catch(function (ex) {
                    handleErrorResponse(form_data.value, ex);
                });
        };

        const onSaveClick = function () {
            clearValidationErrors(form_data.value);
            if (!form_data.value.template_id) form_data.value.template_id_error = $t("Required field");
            if (form_data.value.person_id == null) form_data.value.person_id_error = $t("Required field");

            if (!hasValidationErrors(form_data.value)) {
                if (form_data.value.action === 'replace_jobs') {
                    setConfirmOptions({
                        title: $t("Are you sure?"),
                        text: $t("This will delete all tasks from client plan and put only selected template’s tasks in it"),
                        confirm_text: $t("Continue"),
                        cancel_text: $t("Cancel"),
                        action: onConfirmClick
                    });
                    openConfirmDialog();
                    is_loading.value = true
                } else {
                    setConfirmOptions({
                        title: $t("Notice!"),
                        text: $t("Adding template will be ready in a minute. You can continue with work."),
                        confirm_text: $t("Continue"),
                        cancel_text: $t("Cancel"),
                        action: onConfirmClick
                    });
                    openConfirmDialog();
                    is_loading.value = true
                }
            }
        };

        load_data();

        return {
            is_loading,
            closeDialog,
            users,
            form_data,
            templates,
            loading,
            onSaveClick,
            onConfirmClick,
        };
    }
};
</script>
